import StockAdjustModal from './StockAdjustModal';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import SetupGuard from '../components/SetupGuard';

const ProductsPage = () => {
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [locationsList, setLocationsList] = useState([]);
  const [ingredientsList, setIngredientsList] = useState([]);
  const [adjustingProduct, setAdjustingProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [viewMode, setViewMode] = useState('card');
  const [openReorderId, setOpenReorderId] = useState(null);
   
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await loadData(currentUser.uid);
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, []);

  const loadData = async (uid) => {
    loadLocationsList(uid);
    loadIngredientsList(uid);
    try {
      const db = getFirestore();
      
      // Load categories
      const categoriesSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'categories'), orderBy('order'))
      );
      const cats = categoriesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setCategories(cats);
      
      if (cats.length > 0) {
        // Deep link from Ingredients "Used In": /admin/products?category=X&product=Y
        const targetCatId = searchParams.get('category');
        const targetCat = targetCatId ? cats.find(c => c.id === targetCatId) : null;
        setSelectedCategory(targetCat || cats[0]);
        const targetProdId = searchParams.get('product');
        if (targetCat && targetProdId && !deepLinkHandled.current) {
          deepLinkHandled.current = true;
          try {
            const pdoc = await getDocs(query(collection(db, 'users', uid, 'categories', targetCat.id, 'products')));
            const found = pdoc.docs.find(d => d.id === targetProdId);
            if (found) {
              setEditingProduct({ id: found.id, ...found.data() });
              setShowProductModal(true);
            }
          } catch (e) { console.warn('product deep link failed:', e); }
          setSearchParams({}, { replace: true }); // consume the params once
        }
        await loadProducts(uid, (targetCat || cats[0]).id);
      }
      
      // Load product options
      const optionsSnapshot = await getDocs(collection(db, 'users', uid, 'productOptions'));
      const opts = optionsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setProductOptions(opts);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadIngredientsList = async (uid) => {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, 'users', uid, 'ingredients'));
      setIngredientsList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.warn('[Products] ingredients load failed', e); }
  };

  const loadLocationsList = async (uid) => {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, 'users', uid, 'locations'));
      setLocationsList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Error loading locations for product assignment:', e);
    }
  };

  const loadProducts = async (uid, categoryId) => {
    try {
      const db = getFirestore();
      const productsSnapshot = await getDocs(
        query(collection(db, 'users', uid, 'categories', categoryId, 'products'), orderBy('order'))
      );
      const prods = productsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setProducts(prods);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    if (user) {
      loadProducts(user.uid, category.id);
    }
  };

  const filteredProducts = searchQuery 
    ? products.filter(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : products;

  const handleDeleteProduct = async (product) => {
    if (!user || !selectedCategory) return;
    
    const result = await Swal.fire({
      title: 'Delete Product?',
      text: `Are you sure you want to delete "${product.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    
    if (result.isConfirmed) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'categories', selectedCategory.id, 'products', product.id));
        await loadProducts(user.uid, selectedCategory.id);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Product has been deleted', 'success');
      } catch (error) {
        console.error('Error deleting product:', error);
        Swal.fire('Error', 'Failed to delete product', 'error');
      }
    }
  };

  const handleReorderProduct = async (product, action) => {
    if (!user || !selectedCategory) return;
    
    const sortedProducts = [...products].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIndex = sortedProducts.findIndex(p => p.id === product.id);
    if (currentIndex === -1) return;
    
    let newOrder = product.order || 0;
    
    if (action === 'top') {
      const minOrder = sortedProducts[0]?.order || 0;
      newOrder = minOrder - 1;
    } else if (action === 'up' && currentIndex > 0) {
      const prevProduct = sortedProducts[currentIndex - 1];
      newOrder = (prevProduct.order || 0) - 1;
    } else if (action === 'down' && currentIndex < sortedProducts.length - 1) {
      const nextProduct = sortedProducts[currentIndex + 1];
      newOrder = (nextProduct.order || 0) + 1;
    }
    
    try {
      const db = getFirestore();
      await setDoc(
        doc(db, 'users', user.uid, 'categories', selectedCategory.id, 'products', product.id),
        { order: newOrder },
        { merge: true }
      );
      await loadProducts(user.uid, selectedCategory.id);
    } catch (error) {
      console.error('Error reordering product:', error);
    }
  };

  const handleSaveProduct = async (productData) => {
    if (!user || !selectedCategory) return;
    
    try {
      const db = getFirestore();
      // Move-to-category: when the target differs, write the SAME doc id under
      // the new category and delete the old one (reusing the id lets the POS
      // sync reassign its local copy instead of duplicating).
      const targetCategoryId = productData.moveToCategoryId || selectedCategory.id;
      const targetCategory = categories.find(c => c.id === targetCategoryId) || selectedCategory;
      const moving = !!editingProduct && targetCategoryId !== selectedCategory.id;
      const productRef = editingProduct 
        ? doc(db, 'users', user.uid, 'categories', targetCategoryId, 'products', editingProduct.id)
        : doc(collection(db, 'users', user.uid, 'categories', targetCategoryId, 'products'));
      
      const originalPrice = parseFloat(productData.price) || 0;
      let salePrice = originalPrice;
      if (productData.onSale) {
        if (productData.saleType === 'percentage') {
          salePrice = originalPrice * (1 - (parseFloat(productData.saleValue) || 0) / 100);
        } else {
          salePrice = originalPrice - (parseFloat(productData.saleValue) || 0);
        }
        salePrice = Math.max(0, salePrice);
      }

      const data = {
        name: productData.name,
        // Kitchen-facing short name (workzone tickets / KDS). Never shown
        // to customers; empty string = fall back to the full name.
        abbreviation: (productData.abbreviation || '').trim(),
        description: productData.description,
        imageUrl: productData.imageUrl || null,
        price: originalPrice,
        onSale: productData.onSale || false,
        saleType: productData.saleType || 'percentage',
        saleValue: parseFloat(productData.saleValue) || 0,
        salePrice: salePrice,
        available: productData.available !== false,
        acceptSpecialRequirement: productData.acceptSpecialRequirement || false,
        optionIds: productData.optionIds || [],
        // Location scoping (like categories): EMPTY/missing = available at ALL
        // locations (backward compatible). Non-empty = only those locations.
        availableLocations: productData.availableLocations || [],
        // Inventory (opt-in): OFF by default — nothing changes for merchants
        // who never touch this.
        // Mutually exclusive: stock keeps stock fields and drops the recipe;
        // recipe keeps the BOM and drops stock counts; none drops both.
        inventoryMode: productData.inventoryMode || null,
        trackStock: productData.inventoryMode != null,
        stockScope: productData.inventoryMode === 'stock' ? (productData.stockScope === 'byLocation' ? 'byLocation' : 'global') : 'global',
        stockQty: productData.inventoryMode === 'stock' && productData.stockScope !== 'byLocation' ? (parseInt(productData.stockQty, 10) || 0) : 0,
        stockByLocation: productData.inventoryMode === 'stock' && productData.stockScope === 'byLocation'
          ? Object.fromEntries(Object.entries(productData.stockByLocation || {}).map(([k, v]) => [k, parseInt(v, 10) || 0]))
          : {},
        recipe: productData.inventoryMode === 'recipe'
          ? (productData.recipe || []).filter(r => r.ingredientId && (parseFloat(r.qty) || 0) > 0)
              .map(r => ({
                ingredientId: r.ingredientId,
                // Persist the name too — the POS/admin recipe editors render
                // ingredientName (id-only rows showed up blank there).
                ingredientName: r.ingredientName || ingredientsList.find(i => i.id === r.ingredientId)?.name || '',
                qty: parseFloat(r.qty),
              }))
          : [],
        order: productData.order || 0,
        categoryId: targetCategoryId,
        categoryName: targetCategory.name || '',
        updatedAt: new Date(),
      };
      
      if (!editingProduct) {
        data.createdAt = new Date();
      } else if (moving) {
        // Preserve the original creation date across the move.
        data.createdAt = editingProduct.createdAt || new Date();
      }
      
      await setDoc(productRef, data, { merge: true });
      if (moving) {
        await deleteDoc(doc(db, 'users', user.uid, 'categories', selectedCategory.id, 'products', editingProduct.id));
      }
      
      setShowProductModal(false);
      setEditingProduct(null);
      await loadProducts(user.uid, selectedCategory.id);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', editingProduct ? 'Product updated' : 'Product created', 'success');
    } catch (error) {
      console.error('Error saving product:', error);
      Swal.fire('Error', 'Failed to save product', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (loading) {
    return (
      <AdminLayout title="Products">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Products">
      <SetupGuard prerequisites={['category']}>
      <div className="products-page">
        {/* Category Selector */}
        {categories.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex overflow-auto pb-2" style={{ gap: '12px' }}>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    className={`btn ${selectedCategory?.id === category.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{
                      minWidth: '120px',
                      borderRadius: '12px',
                      backgroundColor: selectedCategory?.id === category.id ? '#ff6b35' : undefined,
                      borderColor: selectedCategory?.id === category.id ? '#ff6b35' : undefined,
                    }}
                    onClick={() => handleCategorySelect(category)}
                  >
                    <i className="fas fa-tag mr-2"></i>
                    {category.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Search and Add */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text"><i className="fas fa-search"></i></span>
              </div>
              <input
                type="text"
                className="form-control"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <div className="input-group-append">
                  <button className="btn btn-outline-secondary" onClick={() => setSearchQuery('')}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="col-md-6 text-right">
            <div className="btn-group mr-2">
              <button
                className={`btn btn-sm ${viewMode === 'card' ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={viewMode === 'card' ? { backgroundColor: '#ff6b35', borderColor: '#ff6b35' } : {}}
                onClick={() => setViewMode('card')}
              >
                <i className="fas fa-th-large"></i>
              </button>
              <button
                className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={viewMode === 'list' ? { backgroundColor: '#ff6b35', borderColor: '#ff6b35' } : {}}
                onClick={() => setViewMode('list')}
              >
                <i className="fas fa-list"></i>
              </button>
            </div>
            <button 
              className="btn btn-primary"
              style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}
              onClick={() => { setEditingProduct(null); setShowProductModal(true); }}
              disabled={!selectedCategory}
            >
              <i className="fas fa-plus mr-2"></i>Add Product
            </button>
          </div>
        </div>

        {/* Products List */}
        {categories.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-tags fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">No categories yet</h4>
            <p className="text-muted">Please add a category first</p>
            <button className="btn btn-primary" onClick={() => navigate('/admin/categories')}>
              <i className="fas fa-arrow-right mr-2"></i>Go to Categories
            </button>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-box fa-4x text-muted mb-4"></i>
            <h4 className="text-muted">{searchQuery ? 'No products found' : 'No products in this category'}</h4>
          </div>
        ) : viewMode === 'card' ? (
          <div className="row">
            {filteredProducts.map((product) => (
              <div key={product.id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
                  <div className="card-body">
                    <div className="d-flex align-items-start mb-3">
                      <div 
                        className="d-flex align-items-center justify-content-center mr-3"
                        style={{
                          width: 60,
                          height: 60,
                          borderRadius: 12,
                          background: product.imageUrl ? 'transparent' : '#f8f9fa',
                          overflow: 'hidden',
                        }}
                      >
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <i className="fas fa-shopping-bag text-muted fa-2x"></i>
                        )}
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start">
                          <h5 className="card-title font-weight-bold m-0" style={{ fontSize: '1rem' }}>{product.name}</h5>
                          {product.onSale && (
                            <span className="badge badge-danger ml-2">SALE</span>
                          )}
                        </div>
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                          {product.description}
                        </p>
                      </div>
                    </div>
                    
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="font-weight-bold" style={{ color: '#ff6b35', fontSize: '1.1rem' }}>
                            {product.onSale && product.salePrice !== undefined ? (
                              <>
                                <span style={{ textDecoration: 'line-through', color: '#888', fontSize: '0.9rem', marginRight: 6 }}>
                                  {formatCurrency(product.price || 0)}
                                </span>
                                {formatCurrency(product.salePrice || 0)}
                              </>
                            ) : (
                              formatCurrency(product.price || 0)
                            )}
                          </span>
                          <span className={`badge badge-${product.available ? 'success' : 'danger'}`}>
                            {product.available ? 'Available' : 'Unavailable'}
                          </span>
                        </div>

                    {product.optionIds?.length > 0 && (
                      <div className="mb-2">
                        {product.optionIds.map((optId) => {
                          const opt = productOptions.find(o => o.id === optId);
                          return opt ? (
                            <span key={optId} className="badge badge-info mr-1" style={{ fontSize: '0.75rem' }}>
                              {opt.name}
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                      <div style={{ position: 'relative' }}>
                        <button 
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setOpenReorderId(openReorderId === product.id ? null : product.id)}
                        >
                          <i className="fas fa-arrows-alt-v mr-1"></i>Reorder
                        </button>
                        {openReorderId === product.id && (
                          <div className="dropdown-menu show" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000 }}>
                            <button className="dropdown-item" onClick={() => { handleReorderProduct(product, 'top'); setOpenReorderId(null); }}>
                              <i className="fas fa-angle-double-up mr-2"></i>Move to Top
                            </button>
                            <button className="dropdown-item" onClick={() => { handleReorderProduct(product, 'up'); setOpenReorderId(null); }}>
                              <i className="fas fa-angle-up mr-2"></i>Move Up
                            </button>
                            <button className="dropdown-item" onClick={() => { handleReorderProduct(product, 'down'); setOpenReorderId(null); }}>
                              <i className="fas fa-angle-down mr-2"></i>Move Down
                            </button>
                          </div>
                        )}
                      </div>
                       <div>
                         {product.trackStock === true && (
                           <button
                             className="btn btn-sm btn-outline-warning mr-2"
                             title="Stock adjustment (production / de-production)"
                             onClick={() => setAdjustingProduct(product)}
                           >
                             <i className="fas fa-boxes-stacked"></i>
                           </button>
                         )}
                         <button
                           className="btn btn-sm btn-outline-primary mr-2"
                           onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                         >
                           <i className="fas fa-edit"></i>
                         </button>
                        <button 
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteProduct(product)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th style={{ borderTop: 'none' }}>Product</th>
                    <th style={{ borderTop: 'none' }}>Price</th>
                    <th style={{ borderTop: 'none' }}>Status</th>
                    <th style={{ borderTop: 'none' }}>Options</th>
                    <th style={{ borderTop: 'none', width: 180 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div 
                            className="d-flex align-items-center justify-content-center mr-3"
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: 8,
                              background: product.imageUrl ? 'transparent' : '#f8f9fa',
                              overflow: 'hidden',
                            }}
                          >
                            {product.imageUrl ? (
                              <img src={product.imageUrl} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <i className="fas fa-shopping-bag text-muted"></i>
                            )}
                          </div>
                          <div>
                            <div className="font-weight-bold">{product.name}</div>
                            <small className="text-muted">{product.description}</small>
                            {product.onSale && <span className="badge badge-danger ml-2">SALE</span>}
                          </div>
                        </div>
                      </td>
                      <td className="font-weight-bold" style={{ color: '#ff6b35' }}>
                        {product.onSale && product.salePrice !== undefined ? (
                          <>
                            <span style={{ textDecoration: 'line-through', color: '#888', fontSize: '0.85rem', marginRight: 6 }}>
                              {formatCurrency(product.price || 0)}
                            </span>
                            {formatCurrency(product.salePrice || 0)}
                          </>
                        ) : (
                          formatCurrency(product.price || 0)
                        )}
                      </td>
                      <td>
                        <span className={`badge badge-${product.available ? 'success' : 'danger'}`}>
                          {product.available ? 'Available' : 'Unavailable'}
                        </span>
                      </td>
                      <td>
                        {product.optionIds?.length > 0 && product.optionIds.map((optId) => {
                          const opt = productOptions.find(o => o.id === optId);
                          return opt ? (
                            <span key={optId} className="badge badge-info mr-1" style={{ fontSize: '0.75rem' }}>{opt.name}</span>
                          ) : null;
                        })}
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div style={{ position: 'relative' }}>
                            <button 
                              className="btn btn-sm btn-outline-secondary mr-2"
                              onClick={() => setOpenReorderId(openReorderId === product.id ? null : product.id)}
                            >
                              <i className="fas fa-arrows-alt-v"></i>
                            </button>
                            {openReorderId === product.id && (
                              <div className="dropdown-menu show" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000 }}>
                                <button className="dropdown-item" onClick={() => { handleReorderProduct(product, 'top'); setOpenReorderId(null); }}>
                                  <i className="fas fa-angle-double-up mr-2"></i>Move to Top
                                </button>
                                <button className="dropdown-item" onClick={() => { handleReorderProduct(product, 'up'); setOpenReorderId(null); }}>
                                  <i className="fas fa-angle-up mr-2"></i>Move Up
                                </button>
                                <button className="dropdown-item" onClick={() => { handleReorderProduct(product, 'down'); setOpenReorderId(null); }}>
                                  <i className="fas fa-angle-down mr-2"></i>Move Down
                                </button>
                              </div>
                            )}
                          </div>
                          <button 
                            className="btn btn-sm btn-outline-primary mr-2"
                            onClick={() => { setEditingProduct(product); setShowProductModal(true); }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteProduct(product)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Stock adjustment (production/de-production with BOM preview) */}
        {adjustingProduct && (
          <StockAdjustModal
            product={adjustingProduct}
            locations={locationsList}
            ingredients={ingredientsList}
            defaultLocationId={null}
            onClose={() => setAdjustingProduct(null)}
            onDone={async () => {
              setAdjustingProduct(null);
              if (selectedCategory) await loadProducts(user.uid, selectedCategory.id);
            }}
          />
        )}

        {/* Product Modal */}
        {showProductModal && (
          <ProductFormModal
            locations={locationsList}
            categories={categories}
            product={editingProduct}
            productOptions={productOptions}
            user={user}
            formatCurrency={formatCurrency}
            onSave={handleSaveProduct}
            onClose={() => { setShowProductModal(false); setEditingProduct(null); }}
          />
        )}
      </div>
      </SetupGuard>
    </AdminLayout>
  );
};

const ProductFormModal = ({ product, productOptions, user, formatCurrency, onSave, onClose, locations = [], categories = [], ingredients: ingredientsProp = [] }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    abbreviation: product?.abbreviation || '',
    description: product?.description || '',
    imageUrl: product?.imageUrl || '',
    price: product?.price || 0,
    onSale: product?.onSale || false,
    saleType: product?.saleType || 'percentage',
    saleValue: product?.saleValue || 0,
    salePrice: product?.salePrice || (product?.price || 0),
    available: product?.available !== false,
    acceptSpecialRequirement: product?.acceptSpecialRequirement || false,
    optionIds: product?.optionIds || [],
    availableLocations: product?.availableLocations || [],
    moveToCategoryId: product?.categoryId || '',
    // Inventory mode: none | stock (sell as-is) | recipe (made from
    // ingredients) — mutually exclusive. Legacy: non-empty recipe → recipe,
    // else trackStock → stock.
    inventoryMode: product?.inventoryMode === 'stock' || product?.inventoryMode === 'recipe'
      ? product.inventoryMode
      : ((Array.isArray(product?.recipe) && product.recipe.length > 0) ? 'recipe' : (product?.trackStock === true ? 'stock' : null)),
    trackStock: product?.trackStock === true,
    stockQty: product?.stockQty ?? 0,
    // Scope: 'global' (one pool for the merchant) | 'byLocation' (per site)
    stockScope: product?.stockScope === 'byLocation' ? 'byLocation' : 'global',
    stockByLocation: product?.stockByLocation && typeof product.stockByLocation === 'object' ? { ...product.stockByLocation } : {},
    recipe: Array.isArray(product?.recipe) ? product.recipe.map(r => ({ ingredientId: r.ingredientId || '', ingredientName: r.ingredientName || ingredientsProp.find(i => i.id === r.ingredientId)?.name || '', qty: r.qty ?? 0 })) : [],
  });
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    // Ingredient list for the recipe editor (raw materials module)
    (async () => {
      try {
        const db = getFirestore();
        const snap = await getDocs(collection(db, 'users', user.uid, 'ingredients'));
        setIngredients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) { console.warn('[Products] ingredients load failed', e); }
    })();
  }, [user.uid]);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsSearchQuery, setOptionsSearchQuery] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Explicit validation (HTML5 `required` alone is bypassable).
    if (!formData.name || !formData.name.trim()) {
      Swal.fire('Error', 'Product name is required', 'error');
      return;
    }
    const price = parseFloat(formData.price);
    if (isNaN(price) || price < 0) {
      Swal.fire('Error', 'A valid price (0 or more) is required', 'error');
      return;
    }
    if (formData.onSale) {
      const sv = parseFloat(formData.saleValue);
      if (isNaN(sv) || sv <= 0) {
        Swal.fire('Error', 'Discount value is required when the product is On Sale', 'error');
        return;
      }
    }
    onSave(formData);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    setUploadingImage(true);
    try {
      const storage = getStorage();
      const userName = user.displayName?.toLowerCase().replace(/\s+/g, '-') || 'user';
      const folderPath = `${userName}-${user.uid}/products`;
      const storageRef = ref(storage, `${folderPath}/${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, imageUrl: downloadUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      Swal.fire('Error', 'Failed to upload image', 'error');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleOption = (optionId) => {
    setFormData(prev => ({
      ...prev,
      optionIds: prev.optionIds.includes(optionId)
        ? prev.optionIds.filter(id => id !== optionId)
        : [...prev.optionIds, optionId]
    }));
  };

  const selectedOptions = productOptions.filter(opt => formData.optionIds.includes(opt.id));
  const selectedCount = selectedOptions.length;

  return (
    <>
      <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
        <div className="modal-dialog modal-lg" style={{ maxWidth: 900, margin: '5vh auto', height: '90vh', display: 'flex' }}>
          <div className="modal-content" style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden', borderRadius: 16 }}>
            <div className="modal-header" style={{ flexShrink: 0 }}>
              <h5 className="modal-title font-weight-bold">{product ? 'Edit Product' : 'Add Product'}</h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '24px' }}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Product Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Price *</label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text">$</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Kitchen Abbreviation <span className="text-muted font-weight-normal">(optional)</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. BRR ASADA"
                    value={formData.abbreviation}
                    onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value })}
                  />
                  <small className="text-muted">Shown on kitchen tickets & KDS instead of the full name. Never visible to customers.</small>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Product Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="d-none"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                  />
                  <div 
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      height: 120,
                      borderRadius: 12,
                      border: '2px dashed #e0e0e0',
                      background: '#f8f9fa',
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadingImage ? (
                      <div className="text-center">
                        <img src="/fodyfee_loading.png" alt="Uploading..." style={{ width: 40, height: 40, objectFit: 'contain' }} />
                        <div className="mt-2 text-muted">Uploading...</div>
                      </div>
                    ) : formData.imageUrl ? (
                      <img 
                        src={formData.imageUrl} 
                        alt="Preview" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="text-center">
                        <i className="fas fa-camera fa-2x text-muted"></i>
                        <div className="mt-2 text-muted">Tap to add photo</div>
                      </div>
                    )}
                  </div>
                  {formData.imageUrl && (
                    <div className="mt-2 text-right">
                      <button 
                        type="button" 
                        className="btn btn-sm btn-link text-danger"
                        onClick={() => setFormData({ ...formData, imageUrl: '' })}
                      >
                        <i className="fas fa-trash mr-1"></i>Remove Image
                      </button>
                    </div>
                  )}
                </div>

                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="onSale"
                        checked={formData.onSale}
                        onChange={(e) => setFormData({ ...formData, onSale: e.target.checked })}
                      />
                      <label className="custom-control-label" htmlFor="onSale">On Sale</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="available"
                        checked={formData.available}
                        onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                      />
                      <label className="custom-control-label" htmlFor="available">Available</label>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="custom-control custom-switch">
                      <input
                        type="checkbox"
                        className="custom-control-input"
                        id="acceptSpecialRequirement"
                        checked={formData.acceptSpecialRequirement}
                        onChange={(e) => setFormData({ ...formData, acceptSpecialRequirement: e.target.checked })}
                      />
                      <label className="custom-control-label" htmlFor="acceptSpecialRequirement">Accept Special Requirements</label>
                    </div>
                  </div>
                </div>

                {/* ── Inventory (opt-in): stock tracking + recipe (BOM) ── */}
                <div className="card mb-4" style={{ borderRadius: 12, border: '1px solid #e5e7eb' }}>
                  <div className="card-body p-3">
                    <label className="font-weight-bold d-block mb-1">
                      <i className="fas fa-boxes-stacked mr-1" style={{ color: '#ff6b35' }}></i>Inventory
                    </label>
                    <small className="text-muted d-block mb-2">
                      Off by default. Choose ONE: sell as-is from stock (finished goods — e.g. merch), or made from a recipe (menu items — ingredients deplete per sale). They can't mix.
                    </small>
                    <div className="btn-group btn-group-sm mb-2">
                      <button type="button" className={`btn ${formData.inventoryMode == null ? 'btn-primary' : 'btn-outline-secondary'}`} style={formData.inventoryMode == null ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}} onClick={() => setFormData({ ...formData, inventoryMode: null, trackStock: false })}>No inventory</button>
                      <button type="button" className={`btn ${formData.inventoryMode === 'stock' ? 'btn-primary' : 'btn-outline-secondary'}`} style={formData.inventoryMode === 'stock' ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}} onClick={() => setFormData({ ...formData, inventoryMode: 'stock', trackStock: true })}>Stock (finished goods)</button>
                      <button type="button" className={`btn ${formData.inventoryMode === 'recipe' ? 'btn-primary' : 'btn-outline-secondary'}`} style={formData.inventoryMode === 'recipe' ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}} onClick={() => setFormData({ ...formData, inventoryMode: 'recipe', trackStock: true })}>Recipe (Bill of Materials)</button>
                    </div>

                    {formData.inventoryMode === 'stock' && (
                      <>
                        <div className="form-group mb-2">
                          <label className="small font-weight-bold mb-1 d-block">Stock scope</label>
                          <div className="btn-group btn-group-sm">
                            <button type="button" className={`btn ${formData.stockScope === 'global' ? 'btn-primary' : 'btn-outline-secondary'}`} style={formData.stockScope === 'global' ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}} onClick={() => setFormData({ ...formData, stockScope: 'global' })}>Global (one pool)</button>
                            <button type="button" className={`btn ${formData.stockScope === 'byLocation' ? 'btn-primary' : 'btn-outline-secondary'}`} style={formData.stockScope === 'byLocation' ? { background: '#ff6b35', borderColor: '#ff6b35' } : {}} onClick={() => setFormData({ ...formData, stockScope: 'byLocation' })}>Per location</button>
                          </div>
                        </div>

                        {formData.stockScope === 'global' ? (
                          <div className="row align-items-end">
                            <div className="col-md-4">
                              <label className="small font-weight-bold mb-1">Stock on hand (units)</label>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                min="0"
                                value={formData.stockQty}
                                onChange={(e) => setFormData({ ...formData, stockQty: e.target.value })}
                              />
                            </div>
                            <div className="col-md-8">
                              <small className="text-muted">One pool shared by every location.</small>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <label className="small font-weight-bold mb-1 d-block">Stock per location</label>
                            {(locations || []).map(loc => (
                              <div className="d-flex align-items-center mb-1" style={{ gap: 8 }} key={loc.id}>
                                <small style={{ minWidth: 180 }}>{loc.companyName || loc.locationName}</small>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  style={{ width: 100 }}
                                  min="0"
                                  value={formData.stockByLocation?.[loc.id] ?? ''}
                                  placeholder="0"
                                  onChange={(e) => setFormData({ ...formData, stockByLocation: { ...formData.stockByLocation, [loc.id]: e.target.value } })}
                                />
                              </div>
                            ))}
                          </div>
                        )}

                      </>
                    )}

                    {/* Recipe / Bill of Materials — recipe mode only */}
                    {formData.inventoryMode === 'recipe' && (
                        <div className="mt-3">
                          <label className="small font-weight-bold d-block mb-1">
                            <i className="fas fa-utensils mr-1" style={{ color: '#ff6b35' }}></i>Recipe (Bill of Materials)
                          </label>
                          <small className="text-muted d-block mb-2">Ingredients used to make ONE unit of this product. Selling depletes these automatically.</small>
                          {(formData.recipe || []).map((r, idx) => {
                            const ing = ingredients.find(i => i.id === r.ingredientId);
                            return (
                              <div className="d-flex align-items-center mb-2" style={{ gap: 8 }} key={idx}>
                                <select
                                  className="form-control form-control-sm"
                                  style={{ maxWidth: 260 }}
                                  value={r.ingredientId}
                                  onChange={(e) => {
                                    const recipe = [...formData.recipe];
                                    recipe[idx] = { ...recipe[idx], ingredientId: e.target.value };
                                    setFormData({ ...formData, recipe });
                                  }}
                                >
                                  <option value="">— ingredient —</option>
                                  {ingredients.map(i => (
                                    <option key={i.id} value={i.id}>{i.name} ({i.useUnit || 'unit'})</option>
                                  ))}
                                </select>
                                <input
                                  type="number"
                                  className="form-control form-control-sm"
                                  style={{ width: 110 }}
                                  min="0"
                                  step="0.01"
                                  placeholder="Qty"
                                  value={r.qty}
                                  onChange={(e) => {
                                    const recipe = [...formData.recipe];
                                    recipe[idx] = { ...recipe[idx], qty: e.target.value };
                                    setFormData({ ...formData, recipe });
                                  }}
                                />
                                <small className="text-muted">{ing?.useUnit || ''}</small>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger"
                                  onClick={() => setFormData({ ...formData, recipe: formData.recipe.filter((_, i) => i !== idx) })}
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setFormData({ ...formData, recipe: [...(formData.recipe || []), { ingredientId: '', qty: '' }] })}
                          >
                            <i className="fas fa-plus mr-1"></i>Add ingredient
                          </button>
                          {ingredients.length === 0 && (
                            <small className="text-muted d-block mt-2">
                              No raw materials yet — create them in the new Inventory → Ingredients module first.
                            </small>
                          )}
                        </div>
                    )}
                  </div>
                </div>

                {/* Move to another category (edit mode only) */}
                {product && categories.length > 1 && (
                  <div className="form-group">
                    <label className="font-weight-bold">
                      <i className="fas fa-folder-open mr-1" style={{ color: '#ff6b35' }}></i>Category
                    </label>
                    <select
                      className="form-control"
                      value={formData.moveToCategoryId}
                      onChange={(e) => setFormData({ ...formData, moveToCategoryId: e.target.value })}
                    >
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <small className="text-muted">Moves the product to the selected category when saving.</small>
                  </div>
                )}

                {/* Location assignment (optional). Empty = ALL locations. */}
                <div className="card mb-4" style={{ borderRadius: 12, border: '1px solid #e5e7eb' }}>
                  <div className="card-body p-3">
                    <label className="font-weight-bold d-block mb-1">
                      <i className="fas fa-store mr-1" style={{ color: '#ff6b35' }}></i>
                      Available at Locations
                    </label>
                    <small className="text-muted d-block mb-2">
                      None selected = available at <strong>all</strong> locations. Select specific locations to limit where this product can be ordered (POS, kiosk, online store).
                    </small>
                    <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                      {locations.map(loc => {
                        const sel = (formData.availableLocations || []).includes(loc.id);
                        return (
                          <button
                            key={loc.id}
                            type="button"
                            className={`btn btn-sm ${sel ? 'btn-primary' : 'btn-outline-secondary'}`}
                            style={sel ? { backgroundColor: '#ff6b35', borderColor: '#ff6b35' } : {}}
                            onClick={() => setFormData(prev => ({
                              ...prev,
                              availableLocations: (prev.availableLocations || []).includes(loc.id)
                                ? prev.availableLocations.filter(id => id !== loc.id)
                                : [...(prev.availableLocations || []), loc.id],
                            }))}
                          >
                            {loc.companyName || loc.locationName || 'Location'}
                          </button>
                        );
                      })}
                      {locations.length === 0 && (
                        <span className="text-muted small">No locations found</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sale Discount Fields */}
                {formData.onSale && (
                  <div className="card mb-4" style={{ borderRadius: 12, border: '1px solid #ff6b35', background: '#fff3ee' }}>
                    <div className="card-body p-3">
                      <div className="row">
                        <div className="col-md-6">
                          <label style={{ fontSize: 14, fontWeight: 700, color: '#555' }}>Discount Type</label>
                          <div className="btn-group w-100" role="group">
                            <button
                              type="button"
                              className={`btn ${formData.saleType === 'percentage' ? 'btn-primary' : 'btn-outline-secondary'}`}
                              style={formData.saleType === 'percentage' ? { backgroundColor: '#ff6b35', borderColor: '#ff6b35' } : {}}
                              onClick={() => setFormData({ ...formData, saleType: 'percentage' })}
                            >
                              <i className="fas fa-percent mr-1"></i>Percentage
                            </button>
                            <button
                              type="button"
                              className={`btn ${formData.saleType === 'fixed' ? 'btn-primary' : 'btn-outline-secondary'}`}
                              style={formData.saleType === 'fixed' ? { backgroundColor: '#ff6b35', borderColor: '#ff6b35' } : {}}
                              onClick={() => setFormData({ ...formData, saleType: 'fixed' })}
                            >
                              <i className="fas fa-dollar-sign mr-1"></i>Fixed Amount
                            </button>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label style={{ fontSize: 14, fontWeight: 700, color: '#555' }}>
                            {formData.saleType === 'percentage' ? 'Discount %' : 'Discount $'}
                          </label>
                          <div className="input-group">
                            <div className="input-group-prepend">
                              <span className="input-group-text">{formData.saleType === 'percentage' ? '%' : '$'}</span>
                            </div>
                            <input
                              type="number"
                              step={formData.saleType === 'percentage' ? '1' : '0.01'}
                              min="0"
                              max={formData.saleType === 'percentage' ? '100' : (formData.price || 0)}
                              className="form-control"
                              value={formData.saleValue}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                let newPrice = formData.price || 0;
                                if (formData.saleType === 'percentage') {
                                  newPrice = (formData.price || 0) * (1 - val / 100);
                                } else {
                                  newPrice = (formData.price || 0) - val;
                                }
                                setFormData({ ...formData, saleValue: val, salePrice: Math.max(0, newPrice) });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-center">
                        <span style={{ fontSize: 14, color: '#888', textDecoration: 'line-through', marginRight: 8 }}>
                          {formatCurrency(formData.price || 0)}
                        </span>
                        <span style={{ fontSize: 20, fontWeight: 800, color: '#dc3545' }}>
                          {formatCurrency(formData.salePrice || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Product Options Section */}
                <div className="form-group">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="m-0 font-weight-bold">Product Options</label>
                    <button 
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setShowOptionsModal(true)}
                    >
                      {selectedCount > 0 ? `${selectedCount} Selected` : 'Select Options'}
                    </button>
                  </div>
                  
                  {selectedOptions.length > 0 ? (
                    <div className="row">
                      {selectedOptions.map((option) => (
                        <div key={option.id} className="col-md-6 mb-3">
                          <div className="card" style={{ borderRadius: '12px', border: '1px solid #e0e0e0' }}>
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="font-weight-bold m-0" style={{ fontSize: '0.95rem' }}>{option.name}</h6>
                                <button 
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-0"
                                  onClick={() => toggleOption(option.id)}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                              <div className="d-flex mb-2" style={{ gap: '16px' }}>
                                <small className="text-muted">
                                  <i className={`fas ${option.optionRequired ? 'fa-check-circle text-success' : 'fa-times-circle text-muted'} mr-1`}></i>
                                  Required
                                </small>
                                <small className="text-muted">
                                  <i className={`fas ${option.allowMultipleSelection ? 'fa-check-circle text-success' : 'fa-times-circle text-muted'} mr-1`}></i>
                                  Multi
                                </small>
                              </div>
                              {option.options && option.options.length > 0 && (
                                <div className="pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                                  <small className="text-muted">
                                    {option.options.map((item, idx) => (
                                      <span key={idx}>
                                        {item.name}{item.extraFee > 0 ? ` (+$${item.extraFee.toFixed(2)})` : ''}
                                        {idx < option.options.length - 1 ? ', ' : ''}
                                      </span>
                                    ))}
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4" style={{ background: '#f8f9fa', borderRadius: '12px' }}>
                      <p className="text-muted mb-2">No options selected</p>
                      <button 
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setShowOptionsModal(true)}
                      >
                        <i className="fas fa-plus mr-1"></i>Select Options
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }}>
                  {product ? 'Update' : 'Create'} Product
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Options Selection Modal */}
      {showOptionsModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">Select Product Options</h5>
                <button type="button" className="close" onClick={() => { setShowOptionsModal(false); setOptionsSearchQuery(''); }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {/* Search Bar */}
                <div className="form-group mb-3">
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text"><i className="fas fa-search"></i></span>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search options..."
                      value={optionsSearchQuery}
                      onChange={(e) => setOptionsSearchQuery(e.target.value)}
                    />
                    {optionsSearchQuery && (
                      <div className="input-group-append">
                        <button className="btn btn-outline-secondary" onClick={() => setOptionsSearchQuery('')}>
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {productOptions.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No product options available</p>
                  </div>
                ) : (
                  (optionsSearchQuery.trim()
                    ? productOptions.filter(o => o.name?.toLowerCase().includes(optionsSearchQuery.toLowerCase()))
                    : productOptions
                  ).map((option) => {
                    const isSelected = formData.optionIds.includes(option.id);
                    return (
                      <div 
                        key={option.id}
                        className={`card mb-3 ${isSelected ? 'border-primary' : ''}`}
                        style={{ 
                          borderRadius: '12px', 
                          cursor: 'pointer',
                          border: isSelected ? '2px solid #ff6b35' : '1px solid #e0e0e0'
                        }}
                        onClick={() => toggleOption(option.id)}
                      >
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="custom-control custom-checkbox flex-grow-1">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`modal-opt-${option.id}`}
                                checked={isSelected}
                                onChange={() => {}}
                              />
                              <label 
                                className="custom-control-label font-weight-bold" 
                                htmlFor={`modal-opt-${option.id}`}
                                style={{ cursor: 'pointer' }}
                              >
                                {option.name}
                              </label>
                            </div>
                            <span className="badge badge-light">{option.options?.length || 0} items</span>
                          </div>
                          <div className="d-flex mt-2 ml-4" style={{ gap: '16px' }}>
                            <small className="text-muted">
                              <i className={`fas ${option.optionRequired ? 'fa-check-circle text-success' : 'fa-times-circle text-muted'} mr-1`}></i>
                              Required
                            </small>
                            <small className="text-muted">
                              <i className={`fas ${option.allowMultipleSelection ? 'fa-check-circle text-success' : 'fa-times-circle text-muted'} mr-1`}></i>
                              Multi
                            </small>
                          </div>
                          {option.options && option.options.length > 0 && (
                            <div className="mt-2 pt-2 ml-4" style={{ borderTop: '1px solid #f0f0f0' }}>
                              <small className="text-muted">
                                {option.options.map((item, idx) => (
                                  <span key={idx}>
                                    {item.name}{item.extraFee > 0 ? ` (+$${item.extraFee.toFixed(2)})` : ''}
                                    {idx < option.options.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowOptionsModal(false)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductsPage;
